import React, { useState, useEffect } from 'react';
import { useOrganization } from '../../context/OrganizationContext';
import { organizationsAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import GeneralTab from './tabs/GeneralTab';
import MembersTab from './tabs/MembersTab';
import InvitationsTab from './tabs/InvitationsTab';
import ActivityTab from './tabs/ActivityTab';
import RolesTab from './tabs/RolesTab';
import InviteMemberModal from './modals/InviteMemberModal';
import PageHeader from '../../components/PageHeader';
import { useTranslation } from 'react-i18next';
import './MyTeam.css';

export default function MyTeam() {
  const { context, organizations, switchContext } = useOrganization();
  const navigate = useNavigate();
  const { t } = useTranslation('organization');
  const { t: tc } = useTranslation('common');

  const [activeTab, setActiveTab] = useState('general');
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [error, setError] = useState(null);

  // Load data when context changes
  useEffect(() => {
    if (!context?.isOrganization || !context?.orgId) {
      setLoading(false);
      return;
    }
    loadData();
  }, [context?.orgId]);

  const loadData = async () => {
    if (!context?.orgId) return;

    try {
      setLoading(true);
      setError(null);

      // No pedir lo que el rol no puede ver: el backend gatea invitations con
      // canManageMembers y audit-log con canViewAnalytics — pedirlos sin
      // permiso produce un 403 ESPERABLE. Antes ese 403 rechazaba el
      // Promise.all entero y blanqueaba también members (que sí había
      // cargado): /mi-equipo quedaba roto para todo miembro no-owner.
      const canManageMembers = !!context.member?.permissions?.canManageMembers;
      const canViewAnalytics = !!context.member?.permissions?.canViewAnalytics;

      // allSettled: un fallo REAL de una llamada no tira las demás; el banner
      // de error solo aparece ante fallos de llamadas que sí correspondían.
      const [membersR, invitationsR, auditLogR] = await Promise.allSettled([
        organizationsAPI.listMembers(context.orgId),
        canManageMembers
          ? organizationsAPI.listInvitations(context.orgId)
          : Promise.resolve({ invitations: [] }),
        canViewAnalytics
          ? organizationsAPI.getAuditLog(context.orgId, 50)
          : Promise.resolve({ logs: [] })
      ]);

      if (membersR.status === 'fulfilled') setMembers(membersR.value.members || []);
      if (invitationsR.status === 'fulfilled') setInvitations(invitationsR.value.invitations || []);
      if (auditLogR.status === 'fulfilled') setAuditLog(auditLogR.value.logs || []);

      const failures = [membersR, invitationsR, auditLogR].filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        console.error('Error loading data:', failures.map((f) => f.reason));
        setError(failures[0].reason?.message || t('errorLoadData'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Creación de organizaciones ELIMINADA (decisión de producto 21-jul-2026):
  // una cuenta = una org. La única vía de crear una org es el checkout
  // (stripeWebhook auto-crea la primera org al pagar); una segunda org =
  // cuenta nueva con su propio checkout. La membresía múltiple vía
  // invitación y el selector de orgs siguen intactos.

  // Change member role
  const handleChangeRole = async (memberUid, newRole) => {
    if (!window.confirm(t('confirmations.changeRole', { role: newRole }))) return;

    try {
      await organizationsAPI.updateMemberRole(context.orgId, memberUid, newRole);
      await loadData(); // Reload
    } catch (error) {
      console.error('Error changing role:', error);
      alert(t('errors.changeRole') + (error.message || t('errors.unknownError')));
    }
  };

  // Remove member
  const handleRemoveMember = async (memberUid, memberEmail) => {
    if (!window.confirm(t('confirmations.removeMember', { email: memberEmail }))) return;

    try {
      await organizationsAPI.removeMember(context.orgId, memberUid);
      await loadData(); // Reload
    } catch (error) {
      console.error('Error removing member:', error);
      alert(t('errors.removeMember') + (error.message || t('errors.unknownError')));
    }
  };

  // Invite member
  const handleInviteMember = async (email, role) => {
    try {
      await organizationsAPI.inviteMember(context.orgId, email, role);
      setShowInviteModal(false);
      await loadData(); // Reload
    } catch (error) {
      console.error('Error inviting member:', error);
      alert(t('errors.sendInvitation') + (error.message || t('errors.unknownError')));
    }
  };

  // Cancel invitation
  const handleCancelInvitation = async (inviteId) => {
    if (!window.confirm(t('confirmations.cancelInvitation'))) return;

    try {
      await organizationsAPI.cancelInvitation(context.orgId, inviteId);
      await loadData(); // Reload
    } catch (error) {
      console.error('Error canceling invitation:', error);
      alert(t('errors.cancelInvitation') + (error.message || t('errors.unknownError')));
    }
  };

  // Render tab content
  const renderTabContent = () => {
    if (!context?.organization) {
      // Sin org = nunca pagó (la org se crea en el checkout). El CTA lleva a
      // contratar, no a crear una org — no existe creación sin pago.
      return (
        <div className="empty-state">
          <h3>{t('emptyState.title')}</h3>
          <p>{t('emptyState.description')}</p>
          <button className="btn-primary" onClick={() => navigate('/contratar')}>
            {t('emptyState.goToCheckout')}
          </button>
        </div>
      );
    }

    switch(activeTab) {
      case 'general':
        return (
          <GeneralTab
            organization={context.organization}
            onReload={loadData}
            currentUserRole={context.member?.role}
            canManageOrgSettings={context.member?.permissions?.canManageOrgSettings}
          />
        );
      case 'members':
        return (
          <MembersTab
            members={members}
            currentUserRole={context.member?.role}
            onChangeRole={handleChangeRole}
            onRemoveMember={handleRemoveMember}
          />
        );
      case 'invitations':
        return (
          <InvitationsTab
            invitations={invitations}
            onInvite={() => setShowInviteModal(true)}
            onCancel={handleCancelInvitation}
            canInvite={context.member?.permissions?.canInviteMembers}
          />
        );
      case 'activity':
        return <ActivityTab logs={auditLog} />;
      case 'roles':
        return (
          <RolesTab
            organization={context.organization}
            currentUserRole={context.member?.role}
            orgId={context.orgId}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="my-team-page">
        <div className="loader">{tc('loading')}</div>
      </div>
    );
  }

  return (
    <div className="my-team-page">
      <PageHeader title={t('title')} subtitle={context?.organization?.name} />

      <div className="header-controls">
        {organizations.length > 0 && (
          <select
            className="org-selector"
            value={context?.orgId || ''}
            onChange={(e) => {
              const orgId = e.target.value;
              if (orgId) {
                switchContext('organization', orgId);
              }
            }}
          >
            <option value="">{t('selectOrg')}</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>
                {org.name} ({org.membership?.role})
              </option>
            ))}
          </select>
        )}
        {context?.member?.role && (
          <span className={`role-badge ${context.member.role}`}>
            {context.member.role}
          </span>
        )}
      </div>

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
          <button onClick={loadData}>{tc('retry')}</button>
        </div>
      )}

      <nav className="my-team-tabs">
        <button
          className={activeTab === 'general' ? 'active' : ''}
          onClick={() => setActiveTab('general')}
        >
          {t('tabs.general')}
        </button>
        <button
          className={activeTab === 'members' ? 'active' : ''}
          onClick={() => setActiveTab('members')}
        >
          {t('tabs.members')} {members.length > 0 && `(${members.length})`}
        </button>
        <button
          className={activeTab === 'invitations' ? 'active' : ''}
          onClick={() => setActiveTab('invitations')}
        >
          {t('tabs.invitations')} {invitations.length > 0 && `(${invitations.length})`}
        </button>
        <button
          className={activeTab === 'activity' ? 'active' : ''}
          onClick={() => setActiveTab('activity')}
        >
          {t('tabs.activity')}
        </button>
        <button
          className={activeTab === 'roles' ? 'active' : ''}
          onClick={() => setActiveTab('roles')}
        >
          {t('tabs.roles')}
        </button>
      </nav>

      <div className="my-team-content">
        {renderTabContent()}
      </div>

      {showInviteModal && (
        <InviteMemberModal
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInviteMember}
        />
      )}
    </div>
  );
}
